import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaChevronUp,
  FaWhatsapp,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaApple,
  FaGooglePlay,
  FaChevronRight,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaYoutube,
  FaPaperPlane,
  FaSpinner,
  FaRegHeart,
} from 'react-icons/fa';
import '../styles/Footer.css';

const Footer = () => {
  const [currentYear, setCurrentYear] = useState('');
  const [isBackToTopVisible, setIsBackToTopVisible] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const navigate = useNavigate();

  // Company information
  const companyInfo = {
    name: 'Fahple Ltd.',
    phone: '+94 750771507',
    whatsapp: '+94 750771507',
    email: 'info@fahple.com',
    address: '272, Mill Road, Irakkamam - 03, Sri Lanka',
  };

  // Popular categories
  const categories = [
    'Staples',
    'Beverages',
    'Personal Care',
    'Home Care',
    'Baby Care',
    'Vegetables & Fruits',
    'Snacks & Foods',
    'Dairy & Bakery',
  ];

  // Customer services
  const services = {
    'About Us': '/about',
    'Terms & Conditions': '/terms',
    FAQ: '/faq',
    'Privacy Policy': '/privacy',
    'E-waste Policy': '/e-waste',
    'Cancellation & Return Policy': '/returns',
  };

  // Social media links
  const socialLinks = [
    { platform: 'facebook', icon: FaFacebook, url: '#' },
    { platform: 'twitter', icon: FaTwitter, url: '#' },
    { platform: 'linkedin', icon: FaLinkedin, url: '#' },
    { platform: 'instagram', icon: FaInstagram, url: '#' },
    { platform: 'youtube', icon: FaYoutube, url: '#' },
  ];

  useEffect(() => {
    // Set current year
    setCurrentYear(new Date().getFullYear());

    // Back to top scroll listener
    const handleScroll = () => {
      setIsBackToTopVisible(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleContactClick = (type) => {
    switch (type) {
      case 'whatsapp':
        openWhatsApp();
        break;
      case 'phone':
        makePhoneCall();
        break;
      case 'email':
        sendEmail();
        break;
      case 'address':
        showAddress();
        break;
      default:
        break;
    }
  };

  const openWhatsApp = () => {
    const cleanPhone = companyInfo.whatsapp
      .replace(/\s+/g, '')
      .replace(/-/g, '');
    const message =
      'Hello Fahple, I would like to know more about your services.';
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(
      message
    )}`;

    if (window.confirm('Open WhatsApp to send a message?')) {
      window.open(url, '_blank');
    }
  };

  const makePhoneCall = () => {
    if (window.confirm(`Call ${companyInfo.phone}?`)) {
      window.location.href = `tel:${companyInfo.phone}`;
    }
  };

  const sendEmail = () => {
    window.location.href = `mailto:${companyInfo.email}?subject=Inquiry about Fahple Services`;
  };

  const showAddress = () => {
    alert(
      `Our address: ${companyInfo.address}\n\nWe're located in Irakkamam, Sri Lanka.`
    );
  };

  const handleAppDownload = (store) => {
    if (store === 'appstore') {
      alert('Redirecting to Apple App Store...');
      // window.open('https://apps.apple.com/app/fahple', '_blank');
    } else {
      alert('Redirecting to Google Play Store...');
      // window.open('https://play.google.com/store/apps/details?id=com.fahple', '_blank');
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(newsletterEmail)) {
      setNewsletterMessage('Please enter a valid email address.');
      setTimeout(() => setNewsletterMessage(''), 3000);
      return;
    }

    setNewsletterLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // In real implementation, send to backend
      console.log('Subscribing email:', newsletterEmail);

      setNewsletterMessage('Thank you for subscribing!');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterMessage('Subscription failed. Please try again.');
    } finally {
      setNewsletterLoading(false);
      setTimeout(() => setNewsletterMessage(''), 5000);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleKeyPress = (e, callback, ...args) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback(...args);
    }
  };

  return (
    <>
      {/* Back to Top Button */}
      <button
        className={`back-to-top ${isBackToTopVisible ? 'visible' : ''}`}
        aria-label='Back to top'
        onClick={scrollToTop}
      >
        <FaChevronUp />
      </button>

      <footer role='contentinfo' aria-label='Site footer'>
        <div className='footer-content'>
          {/* Brand and Contact Section */}
          <div className='footer-section footer-brand'>
            <h2>Fahple</h2>
            <p>
              Your trusted partner for advertising and job portal services.
              Connecting businesses with opportunities.
            </p>

            <div className='contact-info'>
              <div
                className='contact-item'
                tabIndex='0'
                role='button'
                aria-label='Contact via WhatsApp'
                onClick={() => handleContactClick('whatsapp')}
                onKeyPress={(e) =>
                  handleKeyPress(e, handleContactClick, 'whatsapp')
                }
              >
                <FaWhatsapp />
                <span>{companyInfo.whatsapp}</span>
              </div>
              <div
                className='contact-item'
                tabIndex='0'
                role='button'
                aria-label='Contact via phone'
                onClick={() => handleContactClick('phone')}
                onKeyPress={(e) =>
                  handleKeyPress(e, handleContactClick, 'phone')
                }
              >
                <FaPhoneAlt />
                <span>{companyInfo.phone}</span>
              </div>
              <div
                className='contact-item'
                tabIndex='0'
                role='button'
                aria-label='Contact via email'
                onClick={() => handleContactClick('email')}
                onKeyPress={(e) =>
                  handleKeyPress(e, handleContactClick, 'email')
                }
              >
                <FaEnvelope />
                <span>{companyInfo.email}</span>
              </div>
              <div
                className='contact-item'
                tabIndex='0'
                role='button'
                aria-label='View address'
                onClick={() => handleContactClick('address')}
                onKeyPress={(e) =>
                  handleKeyPress(e, handleContactClick, 'address')
                }
              >
                <FaMapMarkerAlt />
                <span>{companyInfo.address}</span>
              </div>
            </div>

            <div className='app-download'>
              <h4>Download Our App</h4>
              <div className='app-buttons'>
                <div
                  className='app-button'
                  tabIndex='0'
                  role='button'
                  aria-label='Download from App Store'
                  onClick={() => handleAppDownload('appstore')}
                  onKeyPress={(e) =>
                    handleKeyPress(e, handleAppDownload, 'appstore')
                  }
                >
                  <FaApple />
                  <div className='app-info'>
                    <span className='available'>Download on the</span>
                    <span className='store'>App Store</span>
                  </div>
                </div>
                <div
                  className='app-button'
                  tabIndex='0'
                  role='button'
                  aria-label='Download from Google Play'
                  onClick={() => handleAppDownload('playstore')}
                  onKeyPress={(e) =>
                    handleKeyPress(e, handleAppDownload, 'playstore')
                  }
                >
                  <FaGooglePlay />
                  <div className='app-info'>
                    <span className='available'>Get it on</span>
                    <span className='store'>Google Play</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Categories Section */}
          <div className='footer-section'>
            <h3>Popular Categories</h3>
            <ul className='footer-links' role='list'>
              {categories.map((category, index) => (
                <li key={index}>
                  <a
                    className='footer-link'
                    href={`/products?category=${encodeURIComponent(category)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation(
                        `/products?category=${encodeURIComponent(category)}`
                      );
                    }}
                    role='listitem'
                  >
                    <FaChevronRight />
                    {category}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services & Social Section */}
          <div className='footer-section'>
            <h3>Customer Services</h3>
            <ul className='footer-links' role='list'>
              {Object.entries(services).map(([service, url], index) => (
                <li key={index}>
                  <a
                    className='footer-link'
                    href={url}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation(url);
                    }}
                    role='listitem'
                  >
                    <FaChevronRight />
                    {service}
                  </a>
                </li>
              ))}
            </ul>

            <div className='social-links'>
              {socialLinks.map(({ platform, icon: Icon, url }, index) => (
                <a
                  key={index}
                  href={url}
                  className='social-link'
                  aria-label={`Follow us on ${
                    platform.charAt(0).toUpperCase() + platform.slice(1)
                  }`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Icon />
                </a>
              ))}
            </div>

            <div className='newsletter'>
              <p>Subscribe to our newsletter</p>
              <form
                className='newsletter-form'
                aria-label='Newsletter subscription'
                onSubmit={handleNewsletterSubmit}
              >
                <input
                  type='email'
                  className='newsletter-input'
                  placeholder='Your email address'
                  aria-label='Email address'
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  disabled={newsletterLoading}
                />
                <button
                  type='submit'
                  className='newsletter-button'
                  aria-label='Subscribe to newsletter'
                  disabled={newsletterLoading}
                >
                  {newsletterLoading ? (
                    <FaSpinner className='spinner' />
                  ) : (
                    <FaPaperPlane />
                  )}
                </button>
              </form>
              {newsletterMessage && (
                <div
                  className={`newsletter-message ${
                    newsletterMessage.includes('Thank you')
                      ? 'success'
                      : 'error'
                  }`}
                >
                  {newsletterMessage}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='footer-bottom'>
          <div className='copyright'>
            &copy; {currentYear} {companyInfo.name}. All rights reserved.
          </div>
          <div className='payment-methods'>
            <span>We accept:</span>
            <div className='payment-icons'>
              <div className='payment-icon' aria-label='Visa'>
                Visa
              </div>
              <div className='payment-icon' aria-label='MasterCard'>
                MC
              </div>
              <div className='payment-icon' aria-label='PayPal'>
                PP
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
